import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import DashboardLayout from "../../../Layout/DashboardLayout";
import { companyID, employeeID } from "../../../Common/UIComponents";
import {
  fetchDateFormatList,
  fetchDateFormatConfig,
  saveDateFormatConfig,
} from "../../../API_Service/Settings_Slice";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const toSingle = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? null;
  if (Array.isArray(d)) return d[0] ?? null;
  return d ?? null;
};

// Normalise to string — prevents undefined === undefined matching all cards
const getID = (obj) => String(obj?.DateFormatID ?? obj?.DateFormatId ?? obj?.Id ?? obj?.id ?? "");
const getFmt = (obj) => obj?.DateFormat ?? obj?.Format ?? obj?.dateFormat ?? "";

/** Preview today's date in the given format token string */
function formatPreview(fmt) {
  if (!fmt) return "";
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const MM = String(now.getMonth() + 1).padStart(2, "0");
  const MMM = now.toLocaleString("en-US", { month: "short" });
  const yyyy = String(now.getFullYear());
  return fmt.replace("MMM", MMM).replace("MM", MM).replace("dd", dd).replace("yyyy", yyyy);
}

// ─────────────────────────────────────────────
// OPTION CARD — mirrors TemperatureHumidityConfigIndex's OptionCard
// ─────────────────────────────────────────────
const OptionCard = ({ id, label, selected, onSelect }) => {
  const isActive = selected === id;
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      className={`relative text-left p-5 rounded-2xl border transition-all
        ${isActive
          ? "border-[#08757c] bg-[#08757c]/[0.06] shadow-[0_0_0_1px_#08757c]"
          : "border-[#e5e9e9] bg-white hover:border-[#08757c]/40 hover:bg-[#f7fafa]"
        }`}
    >
      <div className="flex items-start justify-between mb-3">
        {/* Icon box */}
        <div
          className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0
            ${isActive ? "bg-[#08757c] text-white" : "bg-[#f1f4f4] text-[#6e797a]"}`}
        >
          <span className="material-symbols-outlined text-[20px]">calendar_today</span>
        </div>

        {/* Radio dot */}
        <span
          className={`h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
            ${isActive ? "border-[#08757c]" : "border-[#cdd6d7]"}`}
        >
          {isActive && <span className="h-2.5 w-2.5 rounded-full bg-[#08757c]" />}
        </span>
      </div>

      {/* Format label */}
      <p className={`text-sm font-bold mb-1 ${isActive ? "text-[#08757c]" : "text-[#181c1d]"}`}>
        {label}
      </p>

      {/* Live date preview */}
      <p className="text-xs text-[#6e797a] leading-relaxed">
        e.g. {formatPreview(label)}
      </p>
    </button>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────
export default function DateFormatConfig({ embedded = false } = {}) {
  const dispatch = useDispatch();

  const [formatList, setFormatList] = useState([]);
  const [selectedID, setSelectedID] = useState("");  // always a string
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [error, setError] = useState("");

  // ── Load list + company's saved selection ──
  useEffect(() => {
    setIsLoading(true);
    setLoadError(false);
    Promise.all([
      dispatch(fetchDateFormatList()),
      dispatch(fetchDateFormatConfig({ companyID })),
    ])
      .then(([listRes, savedRes]) => {
        const list = toArray(listRes);
        if (list.length === 0) { setLoadError(true); return; }
        setFormatList(list);

        const saved = toSingle(savedRes);
        const savedIDStr = saved ? getID(saved) : "";
        const savedFmt = saved ? getFmt(saved) : "";

        if (savedIDStr !== "") {
          setSelectedID(savedIDStr);
        } else if (savedFmt) {
          const match = list.find((item) => getFmt(item) === savedFmt);
          if (match) setSelectedID(getID(match));
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (id) => {
    setSelectedID(String(id));
    setError("");
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = () => {
    if (!selectedID) { setError("Please select a date format."); return; }

    const selected = formatList.find((item) => getID(item) === selectedID);
    const formatStr = selected ? getFmt(selected) : "";

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    dispatch(
      saveDateFormatConfig({
        CompanyID: companyID,
        CreatedBy: employeeID,
        DateFormatID: selectedID,
        DateFormat: formatStr,
      })
    )
      .then((res) => {
        if (res?.payload?.success) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 4000);
        } else {
          setSaveError("Failed to save. Please try again.");
        }
      })
      .catch(() => setSaveError("Failed to save. Please try again."))
      .finally(() => setIsSaving(false));
  };

  // ─────────────────────────────────────────
  const content = (
    <div
      className={
        embedded
          ? "px-8 py-5 text-[#181c1d]"
          : "px-8 py-8 bg-[#f7fafa] min-h-screen text-[#181c1d]"
      }
    >
      {!embedded && (
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>Administration</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span>Application Configuration</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#08757C] font-semibold">Date Format</span>
        </nav>
      )}

      {!embedded && (
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-[#181c1d]">
            Date Format
          </h1>
          <p className="text-[#3e494a] text-sm mt-1">
            Choose the date display format used across the application.
          </p>
        </div>
      )}

      <div className="bg-white p-6 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
        {/* Section header */}
        <h3 className="text-base font-bold flex items-center gap-2 mb-1">
          <span className="material-symbols-outlined text-[#08757c] text-[20px]">
            calendar_today
          </span>
          Select Date Format
        </h3>
        <p className="text-xs text-[#6e797a] mb-5">
          Choose exactly one — this decides how dates appear throughout the application.
        </p>

        {/* Options */}
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-[#6e797a] text-sm gap-3">
            <span className="material-symbols-outlined animate-spin text-[#08757c]">
              progress_activity
            </span>
            Loading options…
          </div>
        ) : loadError || formatList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
            <span className="material-symbols-outlined text-[28px] text-[#ba1a1a]">error</span>
            <p className="text-sm font-semibold text-[#181c1d]">Couldn't load date format options.</p>
            <p className="text-xs text-[#6e797a]">
              GetDateFormat returned nothing — check the DateFormat master table has active rows.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {formatList.map((item) => {
              const id = getID(item);
              const label = getFmt(item);
              return (
                <OptionCard
                  key={id || label}
                  id={id}
                  label={label}
                  selected={selectedID}
                  onSelect={handleSelect}
                />
              );
            })}
          </div>
        )}

        {/* Validation error */}
        {error && (
          <p className="mt-3 text-xs text-[#ba1a1a] flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">error</span>
            {error}
          </p>
        )}

        {/* Success banner */}
        {saveSuccess && (
          <div className="mt-5 bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
            <span className="material-symbols-outlined text-[20px]">check_circle</span>
            <span className="font-semibold">Date format saved successfully.</span>
          </div>
        )}

        {/* Error banner */}
        {saveError && (
          <div className="mt-5 bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
            <span className="material-symbols-outlined text-[20px]">error</span>
            {saveError}
          </div>
        )}

        {/* Save button */}
        <div className="flex justify-end mt-5">
          <button
            onClick={handleSave}
            disabled={isSaving || formatList.length === 0}
            className="px-8 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined text-[18px] animate-spin">
                  progress_activity
                </span>
                Saving…
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">save</span>
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
}
